/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-underscore-dangle */
/* eslint-disable consistent-return */
/* eslint-disable object-curly-newline */
/* eslint-disable arrow-body-style */
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/naming-convention */
import { DateTimeFormatter, TemporalAccessor, LocalDate, convert, LocalDateTime, nativeJs } from "@js-joda/core";
import { format as dateFnsFormat } from "date-fns";

import valueOf from "./valueOf";
import OffsetDateTimeValue from "./api/core/OffsetDateTimeValue";

export const EPOCH_DATETIME = "1970/01/01 00:00";
const DELIMITTER = "～";

export const DateFormats = {
    YYYYMMDD: "yyyy/MM/dd",
    YYYYMMDDHHMM: "yyyy/MM/dd HH:mm",
    YYYYMMDDHHMMSS: "yyyy/MM/dd HH:mm:ss",
    YYYYMMDD_JP: "yyyy年MM月dd日",
    YYYYMMDDHHMMSSMI: "yyyy/MM/dd HH:mm:ss.SSS",
    YYYYMMDDHHMMSS_WITH_OFFSET: "yyyy-MM-dd HH:mm:ss.SSSSSSS XXX",
};
export type DateFormat = valueOf<typeof DateFormats>;

const datetimeFormatter = (format: DateFormat): DateTimeFormatter => DateTimeFormatter.ofPattern(format);
export const FORMATTER_YYYYMMDD = datetimeFormatter(DateFormats.YYYYMMDD);
export const FORMATTER_YYYYMMDDHHMM = datetimeFormatter(DateFormats.YYYYMMDDHHMM);
export const FORMATTER_YYYYMMDDHHMMSS = datetimeFormatter(DateFormats.YYYYMMDDHHMMSS);
export const FORMATTER_YYYYMMDD_JP = datetimeFormatter(DateFormats.YYYYMMDD_JP);
export const FORMATTER_YYYYMMDDHHMMSSMI = datetimeFormatter(DateFormats.YYYYMMDDHHMMSSMI);
export const FORMATTER_YYYYMMDDHHMMSS_WITH_OFFSET = datetimeFormatter(DateFormats.YYYYMMDDHHMMSS_WITH_OFFSET);

export const isValidDate = (date: Date | undefined): date is Date => {
    if (!date) return false;
    return !Number.isNaN(date.getTime());
};

const parseDateTime = (datetime: string | undefined, format?: DateFormat) => {
    const formatter = format ? datetimeFormatter(format) : FORMATTER_YYYYMMDDHHMM;
    return datetime ? formatter.parse(datetime) : undefined;
};

// 様々なファイルで使う日付作成を共通化
export const currentDate = (): Date => new Date();

/**
 * 任意の日付文字列からJSのDateオブジェクトへ変換する。
 * ※この際に、時刻情報は破棄される。
 * ※diffDayで日付の差分を見る際の使用を想定。
 *
 * @param datetime 日付文字列
 * @param format 日付フォーマット(省略時はyyyy/MM/dd HH:mm形式)
 *
 * @returns 変換後のDate
 *
 * @see diffDay
 */
export const parseJSDate = (datetime: string | undefined, format?: DateFormat): Date | undefined => {
    if (!datetime) {
        return undefined;
    }

    const formatter = format ? datetimeFormatter(format) : FORMATTER_YYYYMMDDHHMM;
    const temporal = formatter.parse(datetime);
    return convert(LocalDate.from(temporal)).toDate();
};

/**
 * 任意の日付文字列からJSのDateオブジェクトへ変換する。
 * ※時刻情報を保持する。
 * ※時刻を含む日付を見る際の使用を想定。
 *
 * @param datetime 日付文字列
 * @param format 日付フォーマット(省略時はyyyy/MM/dd HH:mm形式)
 *
 * @returns 変換後のDate
 *
 * @see diffDay
 */
export const parseJSDateTime = (datetime: string | undefined, format?: DateFormat): Date | undefined => {
    if (!datetime) {
        return undefined;
    }

    const formatter = format ? datetimeFormatter(format) : FORMATTER_YYYYMMDDHHMM;
    const temporal = formatter.parse(datetime);
    return convert(LocalDateTime.from(temporal)).toDate();
};

/**
 * 期間から開始、終了日時をそれぞれ返却する
 * @param kikan 期間文字列 [開始日時 ～ 終了日時]形式であること
 * @returns startDate: 開始日 string, endDate: 終了日 string
 */
export const separateStartAndEndDate = (kikan: string | undefined) => {
    if (!kikan) {
        return { startDate: "", endDate: "" };
    }

    const [startDate = "", endDate = ""] = kikan.split("～").map((v) => String(v.trim()));
    return { startDate, endDate };
};

const formatDate = (datetime: TemporalAccessor | undefined, formatter?: DateTimeFormatter) => {
    if (!datetime) {
        return "";
    }

    const f = formatter || FORMATTER_YYYYMMDD;
    return f.format(datetime);
};

export const formatJSDate = (date: Date, format?: DateFormat) => {
    if (!date) return undefined;
    if (Number.isNaN(date.getTime())) return undefined;

    const zdt = nativeJs(date);
    const formatter = format ? datetimeFormatter(format) : FORMATTER_YYYYMMDD;
    return formatDate(zdt, formatter);
};

export const formatPeriod = (period: string) => {
    if (!period) {
        return "";
    }

    const [_from, _to] = period.split(DELIMITTER);
    const from = (_from || "").trim();
    const to = (_to || "").trim();
    if (!from || !to) {
        return "";
    }

    const datetimeFrom = parseDateTime(from)!;
    const datetimeTo = parseDateTime(to)!;

    const dateFrom = formatDate(datetimeFrom);
    const dateTo = formatDate(datetimeTo);

    return `${dateFrom} ${DELIMITTER} ${dateTo}`;
};

/**
 * Date型から文字列変換した値を返却する
 * @param date Date型日付
 * @param format 日付フォーマット(省略時はyyyy/MM/dd形式)
 * @returns string型日付
 */
export const dateToStringDate = (date: Date, format?: DateFormat): string | undefined => {
    if (!date) return;
    const localDate = LocalDate.of(date.getFullYear(), date.getMonth() + 1, date.getDate());
    const formatter = format ? datetimeFormatter(format) : FORMATTER_YYYYMMDD;
    const stringDate = formatDate(localDate, formatter);
    return stringDate;
};

/**
 * 日付フォーマット：[yyyy/MM/dd hh:mm] ⇒ [yyyy年MM月dd日]
 * @param date 日時の文字列(yyyy/MM/dd hh:mm 形式)
 * @returns フォーマット後の日付文字列(yyyy年MM月dd日 形式)
 */
export const datetime2DateJP = (date: string) => {
    return formatDate(parseDateTime(date), FORMATTER_YYYYMMDD_JP);
};

/**
 * 差分時間（日）を返却する。
 *
 * @param endDateTime 終了日時を表すDate
 * @param startDateTime 開始日時を表すDate
 *
 * @returns 日付の差分(終了 - 開始)
 */
export const diffDay = (endDateTime: Date, startDateTime: Date) => {
    const diffTime = endDateTime.getTime() - startDateTime.getTime();
    const diffDays = Math.floor(diffTime / (24 * 60 * 60 * 1000));
    return diffDays;
};

export const formatOffsetDateTimeValue = (datetime: OffsetDateTimeValue, formatter?: DateFormat) => {
    if (!datetime) {
        return "";
    }
    const _datetime = new Date(datetime.year, datetime.monthValue - 1, datetime.dayOfMonth, datetime.hour, datetime.minute);
    const _formatter = formatter || DateFormats.YYYYMMDDHHMM;
    return dateFnsFormat(_datetime, _formatter);
};

/**
 * 開始日時と終了日時のOffsetDateTimeValue型から文字列変換、結合した値を返却する
 * @param start 開始日時のOffsetDateTimeValue型
 * @param end 終了日時のOffsetDateTimeValue型
 * @returns string型の期間
 */
export const offsetDateTimeValueToPeriod = (start: OffsetDateTimeValue, end: OffsetDateTimeValue) => {
    // YYYY/MM/DD HH:mm ～ YYYY/MM/DD HH:mmの形式に変換して返却
    return `${formatOffsetDateTimeValue(start)} ${DELIMITTER} ${formatOffsetDateTimeValue(end)}`;
};

/**
 * 開始日時と終了日時のString型から結合した値を返却する
 * @param start 開始日時のString型
 * @param end 終了日時のString型
 * @returns string型の期間
 */
export const concatPeriod = (start: string | undefined, end: string | undefined) => {
    if (!start || !end) return "";

    // YYYY/MM/DD (HH:mm) ～ YYYY/MM/DD (HH:mm)の形式に変換して返却
    return `${start} ${DELIMITTER} ${end}`;
};

export const isInPeriod = (career: { jissiKikan: string }) => {
    const { jissiKikan } = career;

    // 実施期間が決まっていない場合は期間内として扱う
    if (!jissiKikan) return true;

    // 実施期間を過ぎていなければ期間内として扱う
    const { endDate } = separateStartAndEndDate(jissiKikan);
    const _end = new Date(endDate);
    _end.setSeconds(0);
    _end.setMilliseconds(0);

    const _current = currentDate();
    _current.setSeconds(0);
    _current.setMilliseconds(0);

    return _end >= _current;
};
